import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import {
  getAgentTaskCreateOptionsAction,
  getAgentTaskDetailsAction,
  getScheduledSendCreateOptionsAction,
  getScheduledSendDetailsAction,
  getScheduledWhatsappTemplatesAction,
  listAgentTasksAction,
  listScheduledSendsAction,
} from "@/server-actions/agent-tasks"

export const AGENT_TASKS_QUERY_KEY = ["agent-tasks"] as const

export function useAgentTasks() {
  return useQuery({
    queryKey: AGENT_TASKS_QUERY_KEY,
    queryFn: async () => {
      const result = await listAgentTasksAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    refetchInterval: 30_000,
  })
}

export function useScheduledSends() {
  return useQuery({
    queryKey: [...AGENT_TASKS_QUERY_KEY, "scheduled-sends"],
    queryFn: async () => {
      const result = await listScheduledSendsAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    refetchInterval: 30_000,
  })
}

export function useScheduledSendCreateOptions(
  enabled: boolean,
  taskType: "send_email" | "send_whatsapp",
  search: string,
) {
  return useInfiniteQuery({
    queryKey: [...AGENT_TASKS_QUERY_KEY, "scheduled-send-options", taskType, search],
    queryFn: async ({ pageParam }) => {
      const result = await getScheduledSendCreateOptionsAction({ task_type: taskType, offset: pageParam, search })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => lastPage.has_more ? pages.length * 40 : undefined,
    enabled,
  })
}

export function useScheduledSendDetails(taskId: string | null) {
  return useQuery({
    queryKey: [...AGENT_TASKS_QUERY_KEY, "scheduled-send-details", taskId],
    queryFn: async () => {
      const result = await getScheduledSendDetailsAction({ task_id: taskId! })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled: Boolean(taskId),
  })
}

export function useScheduledWhatsappTemplates(channelId: string | null) {
  return useQuery({
    queryKey: [...AGENT_TASKS_QUERY_KEY, "whatsapp-templates", channelId],
    queryFn: async () => {
      const result = await getScheduledWhatsappTemplatesAction({ channel_id: channelId! })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled: Boolean(channelId),
  })
}

export function useAgentTaskDetails(taskId: string | null) {
  return useQuery({
    queryKey: [...AGENT_TASKS_QUERY_KEY, "details", taskId],
    queryFn: async () => {
      const result = await getAgentTaskDetailsAction({ task_id: taskId! })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled: Boolean(taskId),
  })
}

export function useAgentTaskCreateOptions(
  enabled: boolean,
  scopeType?: "email_agent" | "message_agent",
) {
  return useQuery({
    queryKey: [...AGENT_TASKS_QUERY_KEY, "create-options", scopeType],
    queryFn: async () => {
      const result = await getAgentTaskCreateOptionsAction({ scope_type: scopeType })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    enabled,
  })
}
